import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchSuperAdminRegistrationAnalytics } from "../api/superadmin";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import "./ReportsSectionCompact.css";

function defaultFilters() {
  const today = new Date();
  const end = today.toISOString().slice(0, 10);
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 29);
  return {
    start_date: startDate.toISOString().slice(0, 10),
    end_date: end,
    platform: "",
    event_type: "",
    role: "",
    status: "",
    billing_status: "",
    source: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
  };
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value ?? 0}</div>
    </div>
  );
}

function BreakdownTable({ title, rows = [] }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
        {title}
      </div>
      <table className="min-w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {rows.length ? rows.slice(0, 8).map((row) => (
            <tr key={`${title}-${row.value}`}>
              <td className="px-4 py-2 text-slate-700">{row.label || row.value || "Unknown"}</td>
              <td className="px-4 py-2 text-right font-semibold text-slate-900">{row.count}</td>
            </tr>
          )) : (
            <tr><td className="px-4 py-4 text-center text-slate-500">No data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function SuperAdminRegistrationAnalytics() {
  const { is_super_admin } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = async (nextFilters = filters) => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSuperAdminRegistrationAnalytics(nextFilters);
      setData(payload);
    } catch (err) {
      setError(err?.message || "Unable to load registration analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (is_super_admin) {
      loadData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_super_admin]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const clearFilters = () => {
    const next = defaultFilters();
    setFilters(next);
    loadData(next);
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const funnel = data?.funnel || {};
  const breakdowns = data?.breakdowns || {};

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
          <ReportPageHeader title="Registration Analytics" closeTo="/superadmin/reports" />
          <div className={`space-y-6 px-6 py-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Registration Analytics"
              subtitle="Track platform growth, source performance, roles, locations, and conversion milestones."
              sections={[
                {
                  id: "filters",
                  title: "Filters",
                  description:
                    "Use the report filters to review registration performance by date, platform, role, type, status, billing status, and location.",
                  anchorId: "sa-registration-analytics-filters",
                  bullets: [
                    "This report uses inferred registration milestones from existing platform records.",
                    "Trend dates are grouped by the event salon or business timezone.",
                    "The operational list lives under Super Admin > Registration Activity.",
                    "Shared location filters above the report can be used with these local filters.",
                  ],
                },
              ]}
              troubleshooting={[
                {
                  title: "Counts look different from activity",
                  body: "Activity shows individual milestones. Analytics summarizes those same milestones after filters are applied.",
                },
              ]}
            />

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div id="sa-registration-analytics-filters" className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className={LABEL_TEXT}>Start date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={filters.start_date} onChange={(event) => updateFilter("start_date", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>End date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={filters.end_date} onChange={(event) => updateFilter("end_date", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Platform</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.platform} onChange={(event) => updateFilter("platform", event.target.value)}>
                    <option value="">All platforms</option>
                    <option value="Ellyanna Booking">Ellyanna Booking</option>
                    <option value="Ellyanna Boothshare">Ellyanna Boothshare</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Role</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.role} onChange={(event) => updateFilter("role", event.target.value)}>
                    <option value="">All roles</option>
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="independent_professional">Independent Professional</option>
                    <option value="technician">Technician</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Type</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.event_type} onChange={(event) => updateFilter("event_type", event.target.value)}>
                    <option value="">All types</option>
                    <option value="user_registered">User registered</option>
                    <option value="salon_registered">Salon registered</option>
                    <option value="marketplace_profile_created">Ellyanna Boothshare profile created</option>
                    <option value="staff_invite_accepted">Staff invite accepted</option>
                    <option value="join_request_submitted">Join request submitted</option>
                    <option value="billing_account_created">Billing account created</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Status</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.status} onChange={(event) => updateFilter("status", event.target.value)} placeholder="Status" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Billing status</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.billing_status} onChange={(event) => updateFilter("billing_status", event.target.value)} placeholder="Billing status" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Source</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.source} onChange={(event) => updateFilter("source", event.target.value)} placeholder="Source" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>City</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.city} onChange={(event) => updateFilter("city", event.target.value)} placeholder="City" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Province / State</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.province} onChange={(event) => updateFilter("province", event.target.value)} placeholder="Province or state" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Country</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.country} onChange={(event) => updateFilter("country", event.target.value)} placeholder="Country" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Postal / ZIP</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.postal_code} onChange={(event) => updateFilter("postal_code", event.target.value)} placeholder="Postal or ZIP" />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button type="button" className={BTN_OUTLINE} onClick={clearFilters}>Clear</button>
                <button type="button" className={BTN_PRIMARY} onClick={() => loadData(filters)} disabled={loading}>
                  {loading ? "Running..." : "Run Report"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SummaryCard label="Total events" value={summary.total_events} />
              <SummaryCard label="New users" value={summary.new_users} />
              <SummaryCard label="Ellyanna Boothshare events" value={summary.myboothshare_events} />
              <SummaryCard label="Independent professionals" value={summary.independent_professionals} />
              <SummaryCard label="Email verified users" value={summary.email_verified_users} />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-3 text-sm font-semibold text-slate-900">Registration funnel</div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <SummaryCard label="Registered" value={funnel.registered} />
                <SummaryCard label="Email verified" value={funnel.email_verified} />
                <SummaryCard label="Setup started" value={funnel.setup_started} />
                <SummaryCard label="Billing added" value={funnel.billing_added} />
                <SummaryCard label="Paid or active" value={funnel.paid_or_active} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <BreakdownTable title="By platform" rows={breakdowns.platform} />
              <BreakdownTable title="By type" rows={breakdowns.event_type} />
              <BreakdownTable title="By role" rows={breakdowns.role} />
              <BreakdownTable title="By source" rows={breakdowns.source} />
              <BreakdownTable title="By city" rows={breakdowns.city} />
              <BreakdownTable title="By province" rows={breakdowns.province} />
              <BreakdownTable title="By timezone" rows={breakdowns.timezone} />
            </div>

            <div className="rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Local date</th>
                    <th className="px-4 py-3 text-right font-semibold">Total</th>
                    <th className="px-4 py-3 text-right font-semibold">Ellyanna</th>
                    <th className="px-4 py-3 text-right font-semibold">Ellyanna Boothshare</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data?.time_series || []).length ? data.time_series.map((row) => (
                    <tr key={row.date}>
                      <td className="px-4 py-3 text-slate-700">{row.date}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{row.total}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{row.ellyanna}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{row.myboothshare}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No trend data found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
